// Home (Workspace 2, screen 1): a greeting, four counters that open the Action center pre-filtered, a filter panel (type,
// client, partner — with counts) and the board of engagement cards in stage columns (or by client / type). Each card: client,
// type, year end, the engine's progress (workpapers final), report due and badges (blocked, approvals, late documents).
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "../../app/context";
import { EmptyState, ErrorState, Icon, IssueStateCount, LoadingRows, StatusPill } from "../../components";
import type { AttentionItem, EngagementHeader, PbcIndexRow } from "../../data/types";
import { fmtDate } from "../../lib/format";
import { useLoad } from "../../lib/hooks";
import { chipOf, stateCounts } from "../../lib/issues";
import { useSettled, visibleItems } from "../../lib/settled";
import { STAGE_COLUMNS, groupBy, homeCounters, reportDeadline, stageColumn, typeLabel, waitsOnMe } from "../../lib/workspace";
import { daysText } from "../engagement/overviewParts";
import { Counter, ProgressBar, Segmented, useManyWs } from "./common";

type GroupBy = "stage" | "client" | "type";
type Facet = "type" | "client" | "partner";
const FACETS: Array<{ id: Facet; label: string; of: (e: EngagementHeader) => string }> = [
  { id: "type", label: "Engagement type", of: (e) => typeLabel(e.engagementType) },
  { id: "client", label: "Client", of: (e) => e.clientName },
  { id: "partner", label: "Partner", of: (e) => e.partner ?? "No partner named" },
];

const wideScreen = () => { try { return window.matchMedia?.("(min-width: 768px)").matches ?? true; } catch { return true; } };

function greeting(now = new Date()) {
  const h = now.getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

export function WsHome() {
  const { ds, me } = useApp();
  const es = useLoad(() => ds.engagements(), [ds]);
  const ids = (es.data ?? []).map((e) => e.id);
  const ws = useManyWs(ids);
  const [params, setParams] = useSearchParams();
  const group = (params.get("group") as GroupBy) || "stage";
  const [filters, setFilters] = useState<Record<Facet, Set<string>>>({ type: new Set(), client: new Set(), partner: new Set() });

  const all = es.data ?? [];
  const shown = all.filter((e) => FACETS.every((f) => !filters[f.id].size || filters[f.id].has(f.of(e))));
  const settled = useSettled();
  const attention = useMemo(() => visibleItems([...(ws.data?.values() ?? [])].flatMap((x) => x.attention), settled), [ws.data, settled]);
  const docs = useMemo(() => [...(ws.data?.values() ?? [])].flatMap((x) => x.pbc), [ws.data]);
  const c = homeCounters(attention, docs, all, me.personName);
  const first = me.personName.split(" ")[0];

  const columns: Array<[string, EngagementHeader[]]> = group === "stage"
    ? STAGE_COLUMNS.map((s) => [s, shown.filter((e) => stageColumn(e) === s)])
    : groupBy(shown, (e) => (group === "client" ? e.clientName : typeLabel(e.engagementType))).sort(([a], [b]) => a.localeCompare(b));

  const toggle = (f: Facet, v: string) => setFilters((o) => { const s = new Set(o[f]); if (s.has(v)) s.delete(v); else s.add(v); return { ...o, [f]: s }; });

  return (
    <div className="mx-auto max-w-[1500px] space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{greeting()}, {first}</h1>
        <p className="text-sm text-muted">Here is where your engagements stand today.</p>
      </header>
      <section aria-label="What needs you" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Counter value={c.approvals} label="need your approval" to="/actions/approval?mine=1" tone="waiting" icon="check" />
        <Counter value={c.questions} label="agent questions" to="/actions/question" tone="agent" icon="help" />
        <Counter value={c.lateDocs} label="late client documents" to="/actions/client_request?subkind=late" tone="blocked" icon="clip" />
        <Counter value={c.reportsDue} label="reports due in 30 days" to="/actions?due=30" icon="clock" />
      </section>
      {es.error && <ErrorState error={es.error} onRetry={es.reload} />}
      <div className="flex flex-col gap-5 md:flex-row">
        <aside aria-label="Filters" className="md:w-56 md:shrink-0">
          <details className="rounded-lg border border-line bg-raised" open={wideScreen()}>
            <summary className="cursor-pointer px-3 py-2 text-sm font-semibold">Filters</summary>
            <div className="space-y-4 border-t border-line p-3">
              {FACETS.map((f) => {
                const counts = groupBy(all, f.of).sort(([a], [b]) => a.localeCompare(b));
                return (
                  <fieldset key={f.id} className="min-w-0">
                    <legend className="mb-1 text-xs font-semibold text-muted">{f.label}</legend>
                    <ul className="space-y-0.5">
                      {counts.map(([v, rows]) => (
                        <li key={v}>
                          <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-sunken">
                            <input type="checkbox" checked={filters[f.id].has(v)} onChange={() => toggle(f.id, v)} className="accent-brand" />
                            <span className="min-w-0 flex-1 truncate">{v}</span><span className="text-xs tabular-nums text-muted">{rows.length}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </fieldset>
                );
              })}
            </div>
          </details>
        </aside>
        <section aria-label="Engagements" className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">{shown.length} engagement{shown.length === 1 ? "" : "s"}</h2>
            <Segmented label="Group by" value={group} onChange={(g) => setParams(g === "stage" ? {} : { group: g }, { replace: true })}
              options={[{ id: "stage", label: "Stage" }, { id: "client", label: "Client" }, { id: "type", label: "Type" }]} />
          </div>
          {es.loading && !es.data ? <LoadingRows rows={4} label="Loading engagements" /> : !all.length ? (
            <EmptyState title="No engagements you can see yet.">A partner adds you to an engagement's team.</EmptyState>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
              {columns.map(([name, rows]) => (
                <section key={name} aria-label={`${name}, ${rows.length}`} className="min-w-0 rounded-lg bg-sunken p-2">
                  <h3 className="mb-2 flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-wide text-muted">
                    <span className="truncate">{name}</span><span className="tabular-nums">{rows.length}</span>
                  </h3>
                  <ul className="space-y-2">
                    {rows.length === 0 && <li className="px-1 py-3 text-center text-xs text-muted">None</li>}
                    {rows.map((e) => <li key={e.id}><EngagementCard e={e} attention={ws.data?.get(e.id)?.attention ?? []} docs={ws.data?.get(e.id)?.pbc ?? []} /></li>)}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export function EngagementCard({ e, attention, docs }: { e: EngagementHeader; attention: AttentionItem[]; docs: PbcIndexRow[] }) {
  const { me } = useApp();
  const due = reportDeadline(e);
  const blocked = attention.filter((a) => a.tab === "blocked").length;
  const approvals = attention.filter((a) => a.tab === "approval" && waitsOnMe(a, me.personName)).length;
  const late = docs.filter((d) => d.status === "late").length;
  const flagged = stateCounts(attention.filter((a) => a.tab === "issue" && a.issue).map((a) => chipOf({ status: a.issue!.status, chip: a.issue!.chip, state: a.issue!.state })));
  return (
    <Link to={`/e/${e.id}`} className="block space-y-2 rounded-md border border-line bg-raised p-3 hover:border-brand" aria-label={`${e.clientName}, ${typeLabel(e.engagementType)}, year ended ${fmtDate(e.fye)}`}>
      <div>
        <p className="break-words text-sm font-semibold">{e.clientName}</p>
        <p className="text-xs text-muted">{typeLabel(e.engagementType)} · FYE {fmtDate(e.fye)}</p>
      </div>
      {e.progress ? <ProgressBar done={e.progress.final} total={e.progress.total} label={`Workpapers final for ${e.clientName}`} />
        : <p className="text-xs text-muted">Progress not published yet</p>}
      <p className="flex items-start gap-1 text-xs text-muted"><Icon name="clock" size={13} className="mt-0.5 shrink-0" />
        {due?.due ? <>Report due {fmtDate(due.due)}{daysText(due) ? ` · ${daysText(due)}` : ""}</> : e.releasedAt ? <>Issued {fmtDate(e.releasedAt)}</> : "Report date not set"}</p>
      {(blocked > 0 || approvals > 0 || late > 0) && (
        <div className="flex flex-wrap gap-1">
          {blocked > 0 && <StatusPill kind="blocked" label={`${blocked} blocked`} />}
          {approvals > 0 && <StatusPill kind="waiting" label={`${approvals} for you`} />}
          {late > 0 && <StatusPill kind="client" label={`${late} late doc${late === 1 ? "" : "s"}`} />}
        </div>
      )}
      {flagged.length > 0 && (
        <p className="flex flex-wrap items-center gap-1 text-xs text-muted" aria-label={`Flagged issues of ${e.clientName}`}>Flagged:
          {flagged.map((f) => <IssueStateCount key={f.state} state={f.state} count={f.count} />)}</p>
      )}
    </Link>
  );
}
